import { useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import LoginDispatcher from "@Components/Login/actions";
import styles from "./BlogDownloadBanner.module.scss";
import { FC, memo, useCallback, useEffect } from "react";
import wrapperHOC from "@Utils/wrapperHOC";

interface BlogDownloadBannerProps {
  heading: string;
  paragraph: string;
  buttonText: string;
  fileUrl: string;
  bannerId: string;
}

const BlogDownloadBanner: FC<BlogDownloadBannerProps> = ({
  heading,
  paragraph,
  buttonText,
  fileUrl,
  bannerId,
}) => {
  const dispatch = useDispatch();
  const { isUserLoggedIn } = useSelector(({ appData }: AppState) => ({
    isUserLoggedIn: appData.isUserLoggedIn,
  }));

  const downloadPDF = useCallback(
    (onLoad = false) => {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.click();

      if (onLoad && window.location.search.includes("downloadPDF=true")) {
        const urlObject = new URL(window.location.href);
        urlObject.searchParams.delete("downloadPDF");
        window.history.pushState({ path: urlObject.toString() }, "", urlObject.toString());
      }
    },
    [fileUrl],
  );

  const openLoginModal = () => {
    const loginDispatcher = new LoginDispatcher(dispatch);
    loginDispatcher.showLoginModal({ openingSource: "blog_pdf_download" });

    // Add downloadPDF param to URL
    const urlParams = new URLSearchParams(window.location.search);
    const downloadPDFParam = urlParams.get("downloadPDF");
    if (downloadPDFParam !== "true") {
      const newurl = `${window.location.href}?downloadPDF=true`;
      window.history.pushState({ path: newurl }, "", newurl);
    }
  };

  const handleClick = () => {
    if (isUserLoggedIn) {
      downloadPDF(false);
    } else {
      openLoginModal();
    }
  };

  // Handle auto-download after login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const downloadPDFParam = urlParams.get("downloadPDF");

    if (downloadPDFParam === "true" && isUserLoggedIn) {
      downloadPDF(true);
    }
  }, [isUserLoggedIn, downloadPDF]);

  return (
    <div
      id={bannerId}
      className={styles.bannerPdfContainer}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={styles.bannerTextSection}>
        <h5 className={styles.bannerHeading}>{heading}</h5>
        <p className={styles.bannerParagraph}>{paragraph}</p>
      </div>
      <span className={styles.button}>{buttonText}</span>
    </div>
  );
};

export default wrapperHOC(memo(BlogDownloadBanner), {
  componentName: "BlogDownloadBanner-Blog",
  showForChina: true,
});
